import { useList } from '@refinedev/core';
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FormField } from '@/components/forms';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TranslationsForm, type TranslationLanguage } from '@/components/translations/TranslationsForm';
import { LanguageSwitcher } from '@/components/translations/LanguageSwitcher';
import type { TranslationData, TranslationFieldConfig } from '@/components/translations/translations-data';
import type { TriggerAction, TriggerTelegramButton } from '../../types';

interface NotificationFormProps {
  action: TriggerAction;
  onChange: (patch: Partial<TriggerAction>) => void;
}

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  flagEmoji?: string;
};

const TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Заголовок', type: 'input' },
  { name: 'message', label: 'Сообщение', type: 'textarea', required: true, rows: 4 },
];

/**
 * NotificationForm
 *
 * Форма действия отправки уведомления с поддержкой мультиязычности
 */
export function NotificationForm({ action, onChange }: NotificationFormProps) {
  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const [activeLanguage, setActiveLanguage] = useState('ru');

  const showTranslations = languages.length > 0;

  // Конвертируем translations из action в TranslationData формат
  // ru берём из основных полей title/message (они являются дефолтом)
  const translationsData: TranslationData = {
    ...(action.title || action.message ? {
      ru: {
        ...(action.title ? { title: action.title } : {}),
        ...(action.message ? { message: action.message } : {}),
      },
    } : {}),
    ...action.translations,
  };

  const handleTranslationsChange = (data: TranslationData) => {
    // ru — это основные поля title/message, не хранятся в translations
    const { ru: ruData, ...otherLangs } = data;

    // Синхронизируем ru → основные поля
    const patch: Partial<TriggerAction> = {};
    if (ruData) {
      if (ruData.title !== undefined) { patch.title = ruData.title || undefined; }
      if (ruData.message !== undefined) { patch.message = ruData.message || undefined; }
    }

    // Убираем пустые языки из translations (без ru)
    const cleaned: Record<string, Record<string, string>> = {};
    for (const [lang, fields] of Object.entries(otherLangs)) {
      const hasContent = Object.values(fields).some((v) => v?.trim());
      if (hasContent) {
        cleaned[lang] = fields;
      }
    }
    patch.translations = Object.keys(cleaned).length > 0 ? cleaned : undefined;

    onChange(patch);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Канал" name="channel">
          <Select
            value={action.channel ?? 'TELEGRAM'}
            onValueChange={(v) => onChange({ channel: v as 'EMAIL' | 'TELEGRAM' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TELEGRAM">Telegram</SelectItem>
              <SelectItem value="EMAIL">Email</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField label="Заголовок (основной язык)" name="title" hint="Дефолтный заголовок">
          <Input
            value={action.title ?? ''}
            onChange={(e) => onChange({ title: e.target.value || undefined })}
            placeholder="Заголовок уведомления"
          />
        </FormField>

        <FormField label="Сообщение (основной язык)" name="message" hint="Дефолтный текст">
          <Input
            value={action.message ?? ''}
            onChange={(e) => onChange({ message: e.target.value || undefined })}
            placeholder="Текст сообщения"
          />
        </FormField>

        <FormField label="Ссылка" name="url" className="md:col-span-2">
          <Input
            value={action.url ?? ''}
            onChange={(e) => onChange({ url: e.target.value || undefined })}
            placeholder="https://example.com/path или {{platformUrl}}/{{userLanguage}}/profile?tab=subscription"
          />
        </FormField>
      </div>

      {action.channel === 'TELEGRAM' && (
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Кнопки Telegram (Mini App / URL)</p>
              <p className="text-xs text-muted-foreground">
                webAppUrl открывает Mini App. Шаблоны: {"{{platformUrl}}"}, {"{{userLanguage}}"}, {"{{channelPassId}}"}.
                Без кнопок при subscriptionId/channelPassId подставится «Продлить».
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const next: TriggerTelegramButton[] = [
                  ...(action.buttons ?? []),
                  {
                    text: 'Продлить',
                    webAppUrl: '{{platformUrl}}/{{userLanguage}}/profile?tab=subscription',
                    translations: {
                      en: { text: 'Renew' },
                      uz: { text: 'Yangilash' },
                    },
                  },
                ];
                onChange({ buttons: next });
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить кнопку
            </Button>
          </div>

          {(action.buttons ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">Кнопок пока нет (будет default «Продлить» / Renew / Yangilash для абонемента/канала)</p>
          )}

          {(action.buttons ?? []).map((button, index) => (
            <div
              key={`tg-btn-${button.text}-${button.webAppUrl ?? ''}-${button.url ?? ''}-${index}`}
              className="space-y-3 rounded-lg border p-3"
            >
              <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
                <Input
                  value={button.text}
                  onChange={(e) => {
                    const buttons = (action.buttons ?? []).map((btn, i) =>
                      i === index ? { ...btn, text: e.target.value } : btn,
                    );
                    onChange({ buttons });
                  }}
                  placeholder="Текст (ru)"
                />
                <Input
                  value={button.webAppUrl ?? ''}
                  onChange={(e) => {
                    const buttons = (action.buttons ?? []).map((btn, i) =>
                      i === index
                        ? { ...btn, webAppUrl: e.target.value || undefined }
                        : btn,
                    );
                    onChange({ buttons });
                  }}
                  placeholder="webAppUrl (Mini App)"
                />
                <Input
                  value={button.url ?? ''}
                  onChange={(e) => {
                    const buttons = (action.buttons ?? []).map((btn, i) =>
                      i === index
                        ? { ...btn, url: e.target.value || undefined }
                        : btn,
                    );
                    onChange({ buttons });
                  }}
                  placeholder="url (браузер)"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const buttons = (action.buttons ?? []).filter((_, i) => i !== index);
                    onChange({ buttons: buttons.length > 0 ? buttons : undefined });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Input
                  value={button.translations?.en?.text ?? ''}
                  onChange={(e) => {
                    const enText = e.target.value;
                    const buttons = (action.buttons ?? []).map((btn, i) => {
                      if (i !== index) {return btn;}
                      const translations = { ...(btn.translations ?? {}) };
                      if (enText.trim()) {
                        translations.en = { text: enText };
                      } else {
                        delete translations.en;
                      }
                      return {
                        ...btn,
                        translations: Object.keys(translations).length > 0 ? translations : undefined,
                      };
                    });
                    onChange({ buttons });
                  }}
                  placeholder="Текст en (Renew)"
                />
                <Input
                  value={button.translations?.uz?.text ?? ''}
                  onChange={(e) => {
                    const uzText = e.target.value;
                    const buttons = (action.buttons ?? []).map((btn, i) => {
                      if (i !== index) {return btn;}
                      const translations = { ...(btn.translations ?? {}) };
                      if (uzText.trim()) {
                        translations.uz = { text: uzText };
                      } else {
                        delete translations.uz;
                      }
                      return {
                        ...btn,
                        translations: Object.keys(translations).length > 0 ? translations : undefined,
                      };
                    });
                    onChange({ buttons });
                  }}
                  placeholder="Текст uz (Yangilash)"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {showTranslations && (
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Переводы inline текстов</p>
            <LanguageSwitcher
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={setActiveLanguage}
              fields={TRANSLATION_FIELDS}
              value={translationsData}
              variant="inline"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Если для языка пользователя есть перевод — будет использован он. Иначе — основной текст выше.
          </p>
          <TranslationsForm
            fields={TRANSLATION_FIELDS}
            value={translationsData}
            onChange={handleTranslationsChange}
            languages={languages}
            activeLanguage={activeLanguage}
            onLanguageChange={setActiveLanguage}
          />
        </div>
      )}
    </div>
  );
}
